import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactLink from '@exo/frontend-common-link';
import { trackEvent } from './gaTagManager';

/**
 * SessionTimeoutModal - Shows when user session is about to expire or has expired
 * Provides option to refresh token or sign in again
 */
export const SessionTimeoutModal = ({ 
  isOpen = false, 
  onRefresh = () => {},
  onSignIn = () => {},
  isLoading = false,
  title = 'Session Expired',
  message = 'Sorry!!, your session has been expired please log in again',
  isRefreshOptionVisible = true,
  reAuthPath = '/auth/employee'
}) => {
  const [localRefreshing, setLocalRefreshing] = useState(false);

  if (!isOpen) return null;

  const handleRefresh = async () => {
    trackEvent('castrol_flow_session_refresh_click', {
      action: 'click',
      button_name: 'session_refresh',
      source: 'session_timeout_modal'
    });
    setLocalRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setLocalRefreshing(false);
    }
  };

  const handleSignIn = () => {
    trackEvent('castrol_flow_session_sign_in_click', {
      action: 'click',
      button_name: 'session_sign_in',
      source: 'session_timeout_modal'
    });
    onSignIn();
  };

  return (
    <div className="session-timeout-modal-overlay" role="dialog" aria-modal="true">
      <div className="session-timeout-modal-content">
        <div className="session-timeout-modal-header">
          <h2>{title}</h2>
          <div className="session-timeout-icon" aria-hidden="true">
            ⏱️
          </div>
        </div>

        <div className="session-timeout-modal-body">
          <p>{message}</p>
        </div>

        <div className="session-timeout-modal-footer">
          {isRefreshOptionVisible && (
            <button
              className="session-timeout-btn session-timeout-btn-primary"
              type="button"
              onClick={handleRefresh}
              disabled={localRefreshing || isLoading}
              aria-label="Refresh session"
            >
              {localRefreshing || isLoading ? 'Refreshing...' : 'Refresh Session'}
            </button>
          )}
          <ReactLink
            href={reAuthPath}
            className="session-timeout-btn session-timeout-btn-secondary"
            onClick={handleSignIn}
            aria-label="Sign in again"
          >
            Sign In Again
          </ReactLink>
        </div>
      </div>

      <style>{`
        .session-timeout-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(2px);
        }

        .session-timeout-modal-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-width: 400px;
          width: 90%;
          padding: 0;
          overflow: hidden;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .session-timeout-modal-header {
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
          color: white;
          padding: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .session-timeout-modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .session-timeout-icon {
          font-size: 24px;
          margin-left: 10px;
        }

        .session-timeout-modal-body {
          padding: 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .session-timeout-modal-body p {
          margin: 0;
          color: #333;
          font-size: 14px;
          line-height: 1.5;
        }

        .session-timeout-modal-footer {
          padding: 16px 20px;
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-direction: column;
        }

        .session-timeout-btn {
          padding: 10px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
        }

        .session-timeout-btn-primary {
          background-color: #4CAF50;
          color: white;
          border: 1px solid #4CAF50;
        }

        .session-timeout-btn-primary:hover {
          background-color: #45a049;
          border-color: #45a049;
        }

        .session-timeout-btn-secondary {
          background-color: #f5f5f5;
          color: #333;
          border: 1px solid #ddd;
        }

        .session-timeout-btn-secondary:hover {
          background-color: #efefef;
          border-color: #999;
        }

        .session-timeout-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        }

        @media (max-width: 600px) {
          .session-timeout-modal-content {
            width: 95%;
            max-width: none;
          }

          .session-timeout-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

SessionTimeoutModal.propTypes = {
  isOpen: PropTypes.bool,
  onRefresh: PropTypes.func,
  onSignIn: PropTypes.func,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  isRefreshOptionVisible: PropTypes.bool,
  reAuthPath: PropTypes.string
};

export default SessionTimeoutModal;
